import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PublicLayout from "../components/PublicLayout";

export default function PendingApprovalPage() {
  const { user, logout, refreshUser } = useAuth(); // add refreshUser
  const navigate = useNavigate();

  // ✅ Poll every 30 seconds — auto-redirect when approved
  useEffect(() => {
    const interval = setInterval(async () => {
      const updated = await refreshUser();
      if (updated?.shop?.status === "approved" && updated?.shop?.is_active) {
        clearInterval(interval);
        navigate("/dashboard");
      }
    }, 30000); // every 30 seconds

  return () => clearInterval(interval);
    }, [navigate, refreshUser]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <PublicLayout>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-5 py-20"
        style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <div className="max-w-lg w-full">

          {/* Card */}
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-10 text-center">

            {/* Icon */}
            <div className="w-20 h-20 bg-amber-50 border-4 border-amber-100 rounded-3xl flex items-center justify-center text-4xl mx-auto mb-6">
              ⏳
            </div>

            <h1 className="text-2xl font-extrabold text-slate-900 mb-3"
              style={{ fontFamily: "'Sora', sans-serif" }}>
              Your Application is Under Review
            </h1>

            <p className="text-slate-500 leading-relaxed mb-6">
              Thank you for registering <strong className="text-slate-900">{user?.shop?.name || "your business"}</strong> on TumaTrack.
              Our team is reviewing your details and will approve your account within <strong className="text-slate-900">24 hours</strong>.
            </p>

            {/* Status Steps */}
            <div className="bg-slate-50 rounded-2xl p-5 mb-6 text-left space-y-4">
              {[
                { icon: "✅", label: "Application submitted",       done: true },
                { icon: "🔍", label: "Business details under review", done: true,  active: true },
                { icon: "📧", label: "Approval notification sent",   done: false },
                { icon: "🚀", label: "Full access granted",          done: false },
              ].map((step) => (
                <div key={step.label} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-sm shrink-0 ${
                    step.done
                      ? step.active
                        ? "bg-amber-100 text-amber-600 animate-pulse"
                        : "bg-green-100 text-green-600"
                      : "bg-slate-100 text-slate-400"
                  }`}>
                    {step.icon}
                  </div>
                  <p className={`text-sm font-medium ${
                    step.done ? "text-slate-900" : "text-slate-400"
                  }`}>
                    {step.label}
                    {step.active && (
                      <span className="ml-2 text-xs bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full">
                        In progress
                      </span>
                    )}
                  </p>
                </div>
              ))}
            </div>

            {/* What happens next */}
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 mb-6 text-left">
              <p className="text-blue-700 font-semibold text-sm mb-2">📋 What happens next?</p>
              <ul className="space-y-1.5 text-blue-600 text-sm">
                <li>• We verify your business details</li>
                <li>• You'll receive a notification once approved</li>
                <li>• Your 7-day free trial starts on approval</li>
                <li>• You can then add drivers and create orders</li>
              </ul>
            </div>

            {/* Contact support */}
            <div className="bg-green-50 border border-green-100 rounded-2xl p-4 mb-6">
              <p className="text-green-700 text-sm font-medium mb-1">
                Need faster approval?
              </p>
              <a
                href="https://wa.me/254700000001?text=Hi, I just registered my business on TumaTrack and need assistance with approval."
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-green-600 font-semibold text-sm hover:text-green-700 transition-colors"
              >
                💬 Message us on WhatsApp
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </a>
            </div>

            {/* Account info */}
            {user && (
              <div className="border border-slate-100 rounded-2xl p-4 mb-6 text-left">
                <p className="text-slate-400 text-xs font-semibold uppercase tracking-wide mb-3">
                  Your Registration
                </p>
                <div className="space-y-2">
                  {[
                    { label: "Name",     value: user.name },
                    { label: "Phone",    value: user.phone },
                    { label: "Business", value: user.shop?.name },
                    { label: "Status",   value: "Pending Approval", highlight: true },
                  ].map((row) => (
                    <div key={row.label} className="flex justify-between items-center">
                      <span className="text-slate-400 text-xs">{row.label}</span>
                      <span className={`text-xs font-semibold ${
                        row.highlight ? "text-amber-500" : "text-slate-900"
                      }`}>
                        {row.value || "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleLogout}
                className="flex-1 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors"
              >
                Sign Out
              </button>
              <a
                href="https://wa.me/254700000001?text=Hi, I just registered my business on TumaTrack and need assistance with approval."
                target="_blank"
                rel="noreferrer"
                className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-semibold transition-colors text-center"
              >
                Contact Support
              </a>
            </div>
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-slate-400 mt-5">
            Powered by{" "}
            <Link to="/" className="text-orange-500 font-semibold">TumaTrack</Link>
            {" "}— Verified Delivery Management for Kenya 🇰🇪
          </p>
        </div>
      </div>
    </PublicLayout>
  );
}